import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { getPaymentMethodStatus, getTransaction } from 'actions/paymentActions';

import {
  StudentsTransactionStatus,
  TransactionStatus,
  VerificationStatus,
} from 'tools/constants/payment';

import {
  GET_PAYMENT_LINK_TRANSACTION_API_URL,
  GET_TRANSACTION_PUBLIC_API_URL,
} from 'tools/urls/payment';

import TransactionVerification from 'components/payPortal/PaymentSection/TransactionVerification';
import type { TransactionParams } from 'components/payPortal/PaymentSection/TransactionVerification';
import { VerificationFlow } from 'components/payPortal/PaymentSection/verificationStatusConfig';

import type { AxiosResponse } from 'axios';

const VALID_STATUSES: VerificationStatus[] = [
  VerificationStatus.SUCCESS,
  VerificationStatus.ERROR,
  VerificationStatus.CANCEL,
];

const fetchPaymentMethodStatus = async (
  id: string,
): Promise<VerificationStatus> => {
  try {
    const response = await getPaymentMethodStatus(id);
    if (response?.data?.paymentMethodID) {
      return VerificationStatus.SUCCESS;
    }
    return VerificationStatus.LOADING;
  } catch {
    return VerificationStatus.LOADING;
  }
};

const fetchPaymentLinkTransactionStatus = async (
  id: string,
): Promise<VerificationStatus> => {
  try {
    const apiUrl = GET_PAYMENT_LINK_TRANSACTION_API_URL.replace(
      '{transactionID}',
      id,
    );
    const response = await getTransaction(apiUrl);
    const status = response?.data?.data?.status;

    if (status === TransactionStatus.COMPLETED)
      return VerificationStatus.SUCCESS;
    if (
      status === TransactionStatus.FAILED ||
      status === TransactionStatus.DECLINED
    )
      return VerificationStatus.ERROR;
    if (
      status === TransactionStatus.PROCESSING ||
      status === StudentsTransactionStatus.PENDING
    )
      return VerificationStatus.LOADING;

    return VerificationStatus.ERROR;
  } catch {
    return VerificationStatus.ERROR;
  }
};

const fetchTransactionStatus = async (
  id: string,
): Promise<VerificationStatus> => {
  try {
    const apiUrl = GET_TRANSACTION_PUBLIC_API_URL.replace(
      '{transactionID}',
      id,
    );

    const res = (await getTransaction(apiUrl)) as AxiosResponse<{
      data: { status: string };
    }>;
    const transactionStatus = res?.data?.data?.status;

    if (transactionStatus === TransactionStatus.COMPLETED)
      return VerificationStatus.SUCCESS;
    if (
      transactionStatus === TransactionStatus.FAILED ||
      transactionStatus === TransactionStatus.DECLINED
    )
      return VerificationStatus.ERROR;
    if (transactionStatus === TransactionStatus.PROCESSING)
      return VerificationStatus.LOADING;

    return VerificationStatus.ERROR;
  } catch {
    return VerificationStatus.ERROR;
  }
};

const TransactionVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isPaymentMethodFlow = searchParams.has('paymentMethodMappingID');
  const isPaymentLinkTransactionFlow = searchParams.has(
    'paymentLinkTransactionID',
  );

  const params = useMemo<TransactionParams | null>(() => {
    const redirectUrl = searchParams.get('redirectUrl');

    if (isPaymentMethodFlow) {
      const paymentMethodMappingID = searchParams.get('paymentMethodMappingID');
      if (!paymentMethodMappingID) return null;
      return {
        status: VerificationStatus.LOADING,
        transactionID: paymentMethodMappingID,
        ...(redirectUrl ? { redirectUrl } : {}),
      };
    }

    if (isPaymentLinkTransactionFlow) {
      const paymentLinkTransactionID = searchParams.get(
        'paymentLinkTransactionID',
      );
      if (!paymentLinkTransactionID) return null;
      return {
        status: VerificationStatus.LOADING,
        transactionID: paymentLinkTransactionID,
        ...(redirectUrl ? { redirectUrl } : {}),
      };
    }

    const transactionID = searchParams.get('transactionID');
    const status = searchParams.get('status') as VerificationStatus | null;

    if (!transactionID || !status) return null;
    if (!VALID_STATUSES.includes(status)) return null;

    return { ...(redirectUrl ? { redirectUrl } : {}), status, transactionID };
  }, [searchParams, isPaymentMethodFlow, isPaymentLinkTransactionFlow]);

  const verificationFlow = useMemo(() => {
    if (isPaymentMethodFlow) return VerificationFlow.ADD_PAYMENT_METHOD;
    if (isPaymentLinkTransactionFlow)
      return VerificationFlow.PAYMENT_LINK_TRANSACTION;

    const hasRedirectUrl = searchParams.has('redirectUrl');
    return hasRedirectUrl
      ? VerificationFlow.TRANSACTION
      : VerificationFlow.TRANSACTION_NO_REDIRECT;
  }, [isPaymentMethodFlow, isPaymentLinkTransactionFlow, searchParams]);

  const fetchStatus = useMemo(() => {
    if (isPaymentMethodFlow) {
      return fetchPaymentMethodStatus;
    }
    if (isPaymentLinkTransactionFlow) {
      return fetchPaymentLinkTransactionStatus;
    }
    return fetchTransactionStatus;
  }, [isPaymentMethodFlow, isPaymentLinkTransactionFlow]);

  const handleRedirect = useCallback(
    (url: string) => {
      try {
        const parsed = new URL(url);
        const paymentPlanID = parsed.searchParams.get('paymentPlanID');
        parsed.searchParams.delete('paymentPlanID');
        parsed.searchParams.delete('status');
        navigate(parsed.pathname + parsed.search, {
          replace: true,
          state: paymentPlanID ? { paymentPlanID } : undefined,
        });
      } catch {
        navigate(url, { replace: true });
      }
    },
    [navigate],
  );

  const isCanceled = searchParams.get('status') === VerificationStatus.CANCEL;

  return (
    <TransactionVerification
      fetchStatus={!isCanceled ? fetchStatus : undefined}
      flow={verificationFlow}
      initialStatus={isCanceled ? VerificationStatus.CANCEL : undefined}
      onRedirect={handleRedirect}
      params={params}
    />
  );
};

export default TransactionVerificationPage;
